(() => {
    'use strict';

    const MONEY_FORMAT = window.Shopify?.money_format || '${{amount}}';
    const QUANTITY_EVENT = 'quantity-selector:update';

    function formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            return window.Shopify.formatMoney(cents, MONEY_FORMAT);
        }

        const value = (Number(cents || 0) / 100).toFixed(2);
        return `$${value}`;
    }

    function getRoot(el) {
        if (!el) return null;

        return (
            el.closest('product-info') ||
            el.closest('.shopify-section') ||
            el.closest('[data-section-id]') ||
            el.closest('form[action^="/cart/add"]') ||
            el.parentElement ||
            null
        );
    }

    function getPriceScope(root) {
        if (!root) return document;

        return (
            root.closest('product-info') ||
            root.closest('.shopify-section') ||
            root.closest('[data-section-id]') ||
            root.parentElement ||
            document
        );
    }

    function getQuantityInput(root) {
        return root?.querySelector('input[name="quantity"]') || null;
    }

    function getVariantInput(root) {
        return root?.querySelector('input[name="id"]') || null;
    }

    function getPricingData(root) {
        const script = root?.querySelector('[data-product-pricing-json]');
        if (!script) {
            console.warn('[product-price-sync] pricing json not found');
            return null;
        }

        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            console.warn('[product-price-sync] Invalid pricing JSON', error);
            return null;
        }
    }

    function getStoredCustomPricing(root) {
        return root?.__customPickerPricingPayload || null;
    }

    function applyPricingDataToDom(root, payload) {
        if (!root || !payload) return;

        const scripts = root.querySelectorAll('[data-product-pricing-json]');
        if (!scripts.length) return;

        scripts.forEach((script) => {
            script.textContent = JSON.stringify(payload);
        });
    }

    function getPricingDataForVariant(root, variantId) {
        let pricing = getPricingData(root);

        if (pricing?.variants?.[variantId]) {
            return pricing;
        }

        const storedPayload = getStoredCustomPricing(root);
        if (storedPayload?.variants?.[variantId]) {
            applyPricingDataToDom(root, storedPayload);
            return storedPayload;
        }

        return pricing;
    }

    function getCurrentQuantity(root, eventQuantity = null) {
        if (Number.isFinite(eventQuantity) && eventQuantity > 0) {
            return eventQuantity;
        }

        const input = getQuantityInput(root);
        const value = parseInt(input?.value || '1', 10);
        return Number.isFinite(value) && value > 0 ? value : 1;
    }

    function getCurrentVariantId(root) {
        const input = getVariantInput(root);
        return input?.value || null;
    }

    function getEffectiveUnitPrice(variantData, quantity) {
        if (!variantData) return 0;

        let unitPrice = Number(variantData.price || 0);
        const breaks = Array.isArray(variantData.price_breaks)
            ? [...variantData.price_breaks]
            : [];

        breaks.sort((a, b) => Number(a.minimum_quantity) - Number(b.minimum_quantity));

        for (const item of breaks) {
            if (quantity >= Number(item.minimum_quantity)) {
                unitPrice = Number(item.price || 0);
            }
        }

        return unitPrice;
    }

    function updateMainPrice(root, totalPrice, compareAtTotal) {
        const scope = getPriceScope(root);
        const priceRoots = scope.querySelectorAll('[data-product-price-root]');

        if (!priceRoots.length) {
            console.warn('[product-price-sync] price root not found');
            return;
        }

        priceRoots.forEach((priceRoot) => {
            const priceContainer = priceRoot.closest('[data-product-price-container]') || priceRoot;
            const priceEl = priceRoot.querySelector('[data-product-price]');
            const compareEl = priceRoot.querySelector('[data-product-compare-price]');
            const compareGroup = compareEl?.closest('[role="group"]');

            if (priceEl) {
                priceEl.textContent = formatMoney(totalPrice);
            }

            if (compareEl) {
                if (compareAtTotal && Number(compareAtTotal) > Number(totalPrice)) {
                    compareEl.textContent = formatMoney(compareAtTotal);
                    compareGroup?.removeAttribute('hidden');
                    priceContainer?.classList.add('price--on-sale');
                } else {
                    compareEl.textContent = '';
                    compareGroup?.setAttribute('hidden', 'hidden');
                    priceContainer?.classList.remove('price--on-sale');
                }
            }
        });
    }

    function updateButtonPrice(root, totalPrice) {
        const priceEls = root?.querySelectorAll('[data-atc-price]');
        if (!priceEls?.length) return;

        priceEls.forEach((el) => {
            el.textContent = formatMoney(totalPrice);
        });
    }

    function syncPricesDeferred(root, options = {}) {
        if (!root) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                syncPrices(root, options);
            });
        });
    }

    function syncThemeContextFromEvent(root, detail = {}) {
        if (!root) return;

        const productId = detail.productId || root.__customPickerProductId || '';
        const productUrl = detail.productUrl || root.__customPickerProductUrl || '';

        if (!productId || !productUrl) return;

        root.querySelectorAll('variant-picker').forEach((picker) => {
            picker.dataset.productId = String(productId);
            picker.dataset.productUrl = String(productUrl);
        });

        root.querySelectorAll('product-form-component').forEach((formComponent) => {
            formComponent.dataset.productId = String(productId);
        });

        root.querySelectorAll('product-price').forEach((priceComponent) => {
            priceComponent.dataset.productId = String(productId);
        });
    }

    function syncPrices(root, options = {}) {
        if (!root) return;

        const variantId = getCurrentVariantId(root);
        if (!variantId) {
            console.warn('[product-price-sync] variant id not found');
            return;
        }

        const pricing = getPricingDataForVariant(root, variantId);
        if (!pricing) return;

        const variantData = pricing.variants?.[variantId];
        if (!variantData) {
            console.warn('[product-price-sync] variant data not found for id:', variantId);
            return;
        }

        const quantity = getCurrentQuantity(root, options.quantity);
        const unitPrice = getEffectiveUnitPrice(variantData, quantity);
        const totalPrice = unitPrice * quantity;

        const compareAtUnit = Number(variantData.compare_at_price || 0);
        const compareAtTotal = compareAtUnit > 0 ? compareAtUnit * quantity : 0;

        updateMainPrice(root, totalPrice, compareAtTotal);
        updateButtonPrice(root, totalPrice);
    }

    function bind(root) {
        if (!root || root.__totalCostSyncBound) return;
        root.__totalCostSyncBound = true;

        const quantityInput = getQuantityInput(root);
        const variantInput = getVariantInput(root);

        if (quantityInput) {
            quantityInput.addEventListener('input', () => syncPrices(root));
            quantityInput.addEventListener('change', () => syncPrices(root));
        }

        if (variantInput) {
            variantInput.addEventListener('change', () => syncPricesDeferred(root));
            variantInput.addEventListener('input', () => syncPricesDeferred(root));

            const observer = new MutationObserver(() => {
                syncPricesDeferred(root);
            });

            observer.observe(variantInput, {
                attributes: true,
                attributeFilter: ['value'],
            });
        }

        syncPrices(root);
    }

    function init() {
        document.querySelectorAll('add-to-cart-component').forEach((component) => {
            const root = getRoot(component);
            if (root) bind(root);
        });
    }

    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('shopify:section:load', init);

    document.addEventListener(QUANTITY_EVENT, (event) => {
        const root = getRoot(event.target);
        if (!root) return;

        const quantity = Number(event.detail?.quantity);
        syncPrices(root, { quantity });
    });

    document.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const root = getRoot(target);
        if (!root) return;

        if (target.matches('input[name="quantity"]')) {
            syncPrices(root);
            return;
        }

        if (target.matches('input[name="id"]')) {
            syncPricesDeferred(root);
        }
    });

    document.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const root = getRoot(target);
        if (!root) return;

        if (target.matches('input[name="quantity"]')) {
            syncPrices(root);
            return;
        }

        if (target.matches('input[name="id"]')) {
            syncPricesDeferred(root);
        }
    });

    document.addEventListener('product:variant-change', (event) => {
        const root = getRoot(event.target);
        if (!root) return;

        syncThemeContextFromEvent(root, event.detail || {});
        syncPricesDeferred(root);
    });

    document.addEventListener('variant:update', (event) => {
        const root = getRoot(event.target);
        if (!root) return;

        const currentVariantId = getCurrentVariantId(root);
        const storedPricing = getStoredCustomPricing(root);

        if (currentVariantId && storedPricing?.variants?.[currentVariantId]) {
            applyPricingDataToDom(root, storedPricing);
        }

        syncPricesDeferred(root);
    });

})();